import React from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

export interface DraggableMapProps {
  lat?: number;
  lng?: number;
  onChange?: (lat: number, lng: number) => void;
  height?: number;
  markerColor?: string;
}

const DraggableMap: React.FC<DraggableMapProps> = ({ lat = 33.8886, lng = 35.4955, onChange, height = 280, markerColor = "#e53935" }) => {
  const mapRef = React.useRef<HTMLDivElement | null>(null);
  const leafletRef = React.useRef<L.Map | null>(null);
  const markerRef = React.useRef<L.Marker | null>(null);

  React.useEffect(() => {
    if (!mapRef.current) return;
    if (leafletRef.current) return; // init once

  const map = L.map(mapRef.current, { doubleClickZoom: false }).setView([lat, lng], 13);
    leafletRef.current = map;

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: "&copy; OpenStreetMap contributors",
    }).addTo(map);

    // Teardrop/pin SVG marker (small) with inner white circle
    const pinWidth = 18; // px
    const pinHeight = 26; // px
    const pinAnchor: [number, number] = [pinWidth / 2, pinHeight];
    const svg = `
      <svg width="${pinWidth}" height="${pinHeight}" viewBox="0 0 18 26" xmlns="http://www.w3.org/2000/svg" style="display:block">
        <defs>
          <filter id="pinShadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="1" stdDeviation="1" flood-color="rgba(0,0,0,0.35)" />
          </filter>
        </defs>
        <path filter="url(#pinShadow)" d="M9 0C4.03 0 0 4.03 0 9c0 6.78 9 17 9 17s9-10.22 9-17C18 4.03 13.97 0 9 0z" fill="${markerColor}"/>
        <circle cx="9" cy="9" r="3.2" fill="#ffffff"/>
      </svg>
    `;
    const pinIcon = L.divIcon({ className: "", html: svg, iconSize: [pinWidth, pinHeight], iconAnchor: pinAnchor });

    const marker = L.marker([lat, lng], { draggable: true, icon: pinIcon }).addTo(map);
    markerRef.current = marker;

    marker.on("dragend", () => {
      const pos = marker.getLatLng();
      onChange?.(pos.lat, pos.lng);
    });

    map.on("dblclick", (e: L.LeafletMouseEvent) => {
      const { latlng } = e;
      marker.setLatLng(latlng);
      onChange?.(latlng.lat, latlng.lng);
    });

    return () => {
      map.remove();
      leafletRef.current = null;
      markerRef.current = null;
    };
  }, []);

  React.useEffect(() => {
    if (leafletRef.current) {
      leafletRef.current.setView([lat, lng]);
    }
    if (markerRef.current) {
      markerRef.current.setLatLng([lat, lng]);
    }
  }, [lat, lng]);

  return <div ref={mapRef} style={{ width: "100%", height }} />;
};

export default DraggableMap;
